import { findById } from '@qi/frequency-db';
import type { Scan } from '@qi/shared';
import { copy } from '@qi/shared';
import {
  Chip,
  ResonanceMark,
  ScoreRing,
  SegmentedControl,
  Skeleton,
  StatCard,
  Waveform,
} from '@qi/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowRight, RefreshCw, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useMe, useRecommendations, useScans } from '../lib/queries.ts';
import { useSessionStore } from '../stores/session-store.ts';

export const Route = createFileRoute('/_app/dashboard')({
  component: Dashboard,
});

type Range = 'today' | 'week' | 'month';

// The headline "Resonance" is the overall wellness read — a blend of the
// wellness sub-scores (TCM isn't persisted per-scan, so the dashboard uses the
// wellness side only). It deliberately excludes the solfeggio peak: affinity to
// a tone isn't a measure of how well you are. Mirrors the results-page composite.
function wellnessComposite(scores: Scan['scores']): number {
  const parts = [
    scores.focus,
    scores.energy,
    scores.recovery,
    scores.emotionalCoherence,
    100 - scores.stressLoad,
  ];
  return Math.round(parts.reduce((sum, v) => sum + v, 0) / parts.length);
}

// The solfeggio peak is affinity, not a score — a low value just means the tone
// doesn't resonate with you. Shown as a plain band, never a gauge.
function affinityBand(v: number): string {
  if (v >= 80) return 'Very high affinity';
  if (v >= 60) return 'High affinity';
  if (v >= 40) return 'Affinity';
  if (v >= 20) return 'Low affinity';
  return 'Very low affinity';
}

function Dashboard() {
  const [range, setRange] = useState<Range>('week');
  const scansQuery = useScans();
  const goal = useSessionStore((s) => s.goal);
  const user = useSessionStore((s) => s.user);
  const recsQuery = useRecommendations(goal);
  const meQuery = useMe();
  const isFreePlan = meQuery.data?.user?.plan === 'free';
  // Guest = no local session, regardless of /v1/me response state.
  const isGuest = !user;

  const scans = scansQuery.data?.scans ?? [];
  const latest = scans[0];
  const isLoading = scansQuery.isLoading;
  const isEmpty = !isLoading && scans.length === 0;

  // "Continue your scan" affordance — true if the user has picked a goal but
  // hasn't completed a scan today.
  const today = new Date().toISOString().slice(0, 10);
  const latestDate = latest?.startedAt.slice(0, 10);
  const scannedToday = latestDate === today;
  const showResumeBanner = !isLoading && goal && !scannedToday;

  const series = (key: keyof Scan['scores']) => scans.map((s) => s.scores[key]).reverse();
  const coherence = scans.map((s) => (wellnessComposite(s.scores) - 50) / 50).reverse();

  return (
    <div className="mx-auto max-w-7xl space-y-10 px-6 py-10">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">Today</p>
          <h1 className="mt-2 font-display text-4xl text-ink-0">Your resonance map</h1>
        </div>
        <SegmentedControl
          ariaLabel="Range"
          value={range}
          onChange={setRange}
          options={[
            { value: 'today', label: 'Today' },
            { value: 'week', label: 'Week' },
            { value: 'month', label: 'Month' },
          ]}
        />
      </header>

      {showResumeBanner && (
        <Link
          to="/scan/bioscan"
          className="group flex flex-col items-start gap-3 rounded-lg border border-aurora/30 bg-surface-1 px-6 py-4 transition-all hover:border-aurora/60 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
          style={{ boxShadow: 'var(--glow-aurora)' }}
        >
          <div className="flex flex-col gap-0.5">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-aurora">
              Pick up where you left off
            </p>
            <p className="text-ink-0">
              Continue your <span className="text-aurora">{goal}</span> protocol with today's scan
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-2 text-sm text-ink-0">
            <span className="font-mono uppercase tracking-[0.18em]">Continue</span>
            <ArrowRight
              size={16}
              strokeWidth={1.8}
              className="transition-transform duration-200 group-hover:translate-x-0.5"
            />
          </span>
        </Link>
      )}

      {/* Guest banner — top priority when nobody is signed in. The free-plan
          upsell only ever appeared for signed-in free-tier users, which left
          guests with no in-app prompt to convert. */}
      {isGuest && !showResumeBanner && (
        <Link
          to="/auth"
          search={{ mode: 'signup' }}
          className="group flex flex-col items-start gap-3 rounded-lg border border-aurora/30 bg-surface-1 px-6 py-4 transition-all hover:border-aurora/60 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
          style={{ boxShadow: 'var(--glow-aurora)' }}
        >
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="inline-flex h-[36px] w-[36px] items-center justify-center rounded-md border border-aurora/40 bg-surface-3 text-aurora"
            >
              <Sparkles size={16} strokeWidth={1.6} />
            </span>
            <div className="flex flex-col gap-0.5">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-aurora">
                You're browsing as a guest
              </p>
              <p className="text-ink-0">
                Sign in to save your scans, track trends, and get goal-tuned protocols.
              </p>
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-2 text-sm text-ink-0">
            <span className="font-mono uppercase tracking-[0.18em]">Sign up free</span>
            <ArrowRight
              size={16}
              strokeWidth={1.8}
              className="transition-transform duration-200 group-hover:translate-x-0.5"
            />
          </span>
        </Link>
      )}

      {/* Upsell — only shown when the resume banner isn't taking the same slot.
          Pricing now lives in marketing, but free-tier users need an in-app
          entry point since the sidebar no longer carries "Upgrade". */}
      {isFreePlan && !showResumeBanner && (
        <Link
          to="/unlock"
          className="group flex flex-col items-start gap-3 rounded-lg border border-violet/30 bg-surface-1 px-6 py-4 transition-all hover:border-violet/60 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
          style={{ boxShadow: 'var(--glow-violet)' }}
        >
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="inline-flex h-[36px] w-[36px] items-center justify-center rounded-md border border-violet/40 bg-surface-3 text-violet"
            >
              <Sparkles size={16} strokeWidth={1.6} />
            </span>
            <div className="flex flex-col gap-0.5">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-violet">
                On the free plan
              </p>
              <p className="text-ink-0">
                Premium unlocks unlimited scans, full trend history, and advanced protocols.
              </p>
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-2 text-sm text-ink-0">
            <span className="font-mono uppercase tracking-[0.18em]">See Premium</span>
            <ArrowRight
              size={16}
              strokeWidth={1.8}
              className="transition-transform duration-200 group-hover:translate-x-0.5"
            />
          </span>
        </Link>
      )}

      {isEmpty ? (
        <EmptyDashboard />
      ) : (
        <>
          <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="rounded-lg border border-hairline bg-surface-1 p-6 lg:col-span-1">
              <p className="mb-4 font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
                {copy.navigation.dashboard}
              </p>
              <div className="flex items-center justify-center">
                {isLoading ? (
                  <Skeleton width={200} height={200} rounded="full" />
                ) : (
                  <ScoreRing score={latest ? wellnessComposite(latest.scores) : 0} />
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:col-span-2 lg:grid-cols-2">
              {isLoading ? (
                <>
                  <StatCardSkeleton />
                  <StatCardSkeleton />
                  <StatCardSkeleton />
                  <StatCardSkeleton />
                </>
              ) : (
                <>
                  <StatCard
                    label="Stress load"
                    value={latest?.scores.stressLoad ?? '—'}
                    meta={describeTrend(series('stressLoad'), { inverse: true })}
                    trend={series('stressLoad')}
                  />
                  <StatCard
                    label="Focus"
                    value={latest?.scores.focus ?? '—'}
                    meta={describeTrend(series('focus'))}
                    trend={series('focus')}
                  />
                  <StatCard
                    label="Recovery"
                    value={latest?.scores.recovery ?? '—'}
                    meta={describeTrend(series('recovery'))}
                    trend={series('recovery')}
                  />
                  <StatCard
                    label="Energy"
                    value={latest?.scores.energy ?? '—'}
                    meta={describeTrend(series('energy'))}
                    trend={series('energy')}
                  />
                </>
              )}
            </div>
          </section>

          <section className="rounded-lg border border-hairline bg-surface-1 p-6">
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
              7-day coherence
            </p>
            {isLoading ? (
              <Skeleton width="100%" height={120} rounded="md" />
            ) : coherence.length > 0 ? (
              <Waveform samples={coherence} width={800} height={120} />
            ) : (
              <Waveform width={800} height={120} />
            )}
          </section>

          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
                Recommended frequencies {goal && <span className="text-ink-3">· for {goal}</span>}
              </p>
              {goal && (
                <button
                  type="button"
                  onClick={() => recsQuery.refetch()}
                  disabled={recsQuery.isFetching}
                  className="inline-flex items-center gap-1.5 rounded-pill border border-hairline px-3 py-1 font-mono text-xs uppercase tracking-[0.18em] text-ink-2 transition-colors hover:border-aurora/40 hover:text-aurora disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <RefreshCw
                    size={11}
                    strokeWidth={2}
                    className={recsQuery.isFetching ? 'animate-spin' : ''}
                  />
                  <span>Refresh</span>
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              {recsQuery.data ? (
                recsQuery.data.protocol.map((step, idx) => {
                  const freq = findById(step.frequencyId);
                  return (
                    <Chip
                      key={step.frequencyId}
                      withDot
                      tone={CHIP_TONES[idx % CHIP_TONES.length]}
                      active={idx === 0}
                    >
                      {freq?.historicalAssociation ?? step.frequencyId}
                    </Chip>
                  );
                })
              ) : recsQuery.isLoading ? (
                <>
                  <Skeleton width={140} height={28} rounded="pill" />
                  <Skeleton width={120} height={28} rounded="pill" />
                  <Skeleton width={160} height={28} rounded="pill" />
                </>
              ) : (
                <p className="text-sm text-ink-2">
                  Pick a goal in{' '}
                  <Link to="/onboarding" className="text-aurora hover:underline">
                    onboarding
                  </Link>{' '}
                  to see your protocol.
                </p>
              )}
            </div>
            {recsQuery.data?.rationale && (
              <p className="text-sm italic text-ink-2">{recsQuery.data.rationale}</p>
            )}
            <p className="text-sm text-ink-2">
              Start a new scan to refresh your protocol —{' '}
              <Link to="/scan/bioscan" className="text-aurora hover:underline">
                begin scan
              </Link>
              .
            </p>
            {scansQuery.isError && (
              <p role="alert" className="text-sm text-rose" aria-live="polite">
                Could not reach the resonance log. Retrying soon.
              </p>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
                Recent scans
              </p>
              <span className="font-mono text-xs uppercase tracking-[0.18em] text-ink-3">
                Showing {Math.min(scans.length, 5)} of {scans.length}
              </span>
            </div>
            <div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
              {scans.slice(0, 5).map((scan, idx) => (
                <div
                  key={scan.id}
                  className={`flex items-center justify-between gap-4 px-6 py-4 ${idx > 0 ? 'border-t border-hairline' : ''}`}
                >
                  <div className="flex flex-col gap-1">
                    <span className="font-mono text-sm text-ink-0">
                      {formatScanDate(scan.startedAt)}
                    </span>
                    <span className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
                      {Math.round(scan.durationMs / 1000)}s · {scan.resonancePeaks.length} peaks ·{' '}
                      {affinityBand(scan.scores.resonance)}
                    </span>
                  </div>
                  <div className="flex items-center gap-6">
                    <Stat label="Resonance" value={wellnessComposite(scan.scores)} accent />
                    <Stat label="Stress" value={scan.scores.stressLoad} />
                    <Stat label="Focus" value={scan.scores.focus} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function StatCardSkeleton() {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-hairline bg-gradient-to-b from-surface-2 to-surface-1 p-6">
      <Skeleton width={80} height={10} />
      <Skeleton width={64} height={32} />
      <Skeleton width="80%" height={20} rounded="sm" />
    </div>
  );
}

function EmptyDashboard() {
  // The scan flow (/scan/bioscan) has no goal gate and handles a missing goal
  // gracefully, so a user who already has a goal starts the scan directly.
  // Only a first-timer without a goal detours through onboarding to pick one.
  const goal = useSessionStore((s) => s.goal);
  return (
    <div className="flex flex-col items-center gap-6 rounded-lg border border-hairline bg-surface-1 px-6 py-16 text-center">
      <div className="text-aurora/40">
        <ResonanceMark size={120} />
      </div>
      <div className="space-y-2">
        <h2 className="font-display text-3xl text-ink-0">
          Your first resonance map will live here.
        </h2>
        <p className="max-w-md text-ink-1">
          Run a 60-second scan. We'll surface the frequencies your nervous system responds to and
          build a protocol around them.
        </p>
      </div>
      <Link to={goal ? '/scan/bioscan' : '/goal'} className="group">
        <span
          className="inline-flex items-center gap-2 rounded-md border border-aurora/40 px-6 py-3 font-medium text-ink-0 transition-colors"
          style={{
            background: 'color-mix(in oklab, var(--aurora) 18%, var(--surface-2))',
            boxShadow: 'var(--glow-aurora)',
          }}
        >
          <span>Begin your first scan</span>
          <span
            aria-hidden="true"
            className="transition-transform duration-200 group-hover:translate-x-0.5"
          >
            →
          </span>
        </span>
      </Link>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <span
        className={`font-mono text-lg tracking-tight tabular-nums ${accent ? 'text-aurora' : 'text-ink-0'}`}
      >
        {value}
      </span>
      <span className="font-mono text-xs uppercase tracking-[0.18em] text-ink-3">{label}</span>
    </div>
  );
}

function formatScanDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const CHIP_TONES = ['aurora', 'violet', 'jade', 'ember', 'rose'] as const;

/**
 * Renders a short qualitative meta string from a numeric trend series. For
 * stress-style metrics where lower is better, pass `inverse: true` so the
 * up/down arrows align with the "softer / heavier" framing.
 */
export function describeTrend(series: number[], opts: { inverse?: boolean } = {}): string {
  if (series.length < 2) return 'baseline';
  const head = series.slice(-3);
  const last = head[head.length - 1] ?? 0;
  const first = head[0] ?? 0;
  const delta = Math.round(last - first);
  if (delta === 0) return 'steady';
  const rising = delta > 0;
  // Arrow always tracks numeric direction. The word carries sentiment so
  // stress-style metrics (inverse) read naturally: dropping stress = "softer".
  const arrow = rising ? '↑' : '↓';
  const word = opts.inverse ? (rising ? 'heavier' : 'softer') : rising ? 'rising' : 'easing';
  return `${arrow} ${Math.abs(delta)} ${word}`;
}
